import React, { useEffect, useMemo, useState } from "react";
import apiPublic from "../../apiPublic";
import "./NewJoinerAssetRequestPage.css";

// Phase 52 (New Joiner Asset Request), task spec §2-4/§36. A no-login
// public intake form -- uses apiPublic.js (the dedicated unauthenticated
// axios instance, Phase 16's own precedent: never src/api.js, which
// attaches the viewer's own session token and redirects to /login on 401,
// neither of which is correct for an anonymous public form). Visual
// language mirrors the Login screen's maroon/gold/cream/Sora/Playfair
// brand via this page's own self-contained NewJoinerAssetRequestPage.css
// (see that file's header comment for why it doesn't depend on
// theme/portalTokens.css).
const LOGO_SRC = "/assets/brand/jaipuria-it360-logo-trimmed.jpg";

const initialForm = {
  employee_name: "",
  employee_username: "",
  employee_code: "",
  designation: "",
  department_id: "",
  company_id: "",
  manager_user_id: "",
  joining_date: "",
  official_email: "",
  location: "",
  remarks: "",
  requester_name: "",
  requester_email: "",
};

export default function NewJoinerAssetRequestPage() {
  const [form, setForm] = useState(initialForm);
  const [assetTypes, setAssetTypes] = useState([]);
  const [selected, setSelected] = useState({}); // { [assetTypeId]: { checked, qty, note } }
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [managers, setManagers] = useState([]);
  const [managersLoading, setManagersLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    apiPublic.get("/public/new-joiner-asset-request/asset-types").then((res) => {
      const rows = res.data?.data || [];
      setAssetTypes(rows);
      const initSel = {};
      rows.forEach((t) => { initSel[t.id] = { checked: false, qty: 1, note: "" }; });
      setSelected(initSel);
    }).catch(() => setAssetTypes([]));

    apiPublic.get("/public/new-joiner-asset-request/companies").then((res) => {
      setCompanies(res.data?.data || []);
    }).catch(() => setCompanies([]));

    apiPublic.get("/public/new-joiner-asset-request/departments").then((res) => {
      setDepartments(res.data?.data || []);
    }).catch(() => setDepartments([]));
  }, []);

  useEffect(() => {
    setManagersLoading(true);
    const params = form.company_id ? { company_id: form.company_id } : {};
    apiPublic.get("/public/new-joiner-asset-request/managers", { params })
      .then((res) => setManagers(res.data?.data || []))
      .catch(() => setManagers([]))
      .finally(() => setManagersLoading(false));
  }, [form.company_id]);

  const setField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const toggleAsset = (id) => setSelected((s) => ({ ...s, [id]: { ...s[id], checked: !s[id]?.checked } }));
  const setAssetQty = (id, qty) => setSelected((s) => ({ ...s, [id]: { ...s[id], qty } }));
  const setAssetNote = (id, note) => setSelected((s) => ({ ...s, [id]: { ...s[id], note } }));

  const selectedItems = useMemo(
    () => Object.entries(selected)
      .filter(([, v]) => v.checked)
      .map(([id, v]) => ({ asset_request_type_id: Number(id), qty: Number(v.qty) || 1, requirement_note: v.note || undefined })),
    [selected]
  );

  const validateClientSide = () => {
    if (!form.employee_name.trim()) return "Employee Name is required.";
    if (!form.employee_username.trim()) return "Employee Username is required.";
    if (!form.designation.trim()) return "Designation is required.";
    if (!form.department_id) return "Department is required.";
    if (!form.company_id) return "Company / Unit is required.";
    if (!form.manager_user_id) return "Reporting Manager is required.";
    if (!form.joining_date) return "Joining Date is required.";
    if (selectedItems.length === 0) return "Select at least one required asset.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const clientError = validateClientSide();
    if (clientError) { setSubmitError(clientError); return; }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = {
        ...form,
        department_id: Number(form.department_id),
        company_id: Number(form.company_id),
        manager_user_id: Number(form.manager_user_id),
        items: selectedItems,
      };
      const res = await apiPublic.post("/public/new-joiner-asset-request", payload);
      setSuccess(res.data?.data || null);
    } catch (err) {
      setSubmitError(err?.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="njar-root">
        <div className="njar-topbar">
          <img className="njar-topbar-logo" src={LOGO_SRC} alt="Jaipuria IT360 Desk" onError={(e) => { e.target.style.display = "none"; }} />
          <span className="njar-topbar-brand">Jaipuria IT360 Desk</span>
        </div>
        <div className="njar-success">
          <div className="njar-success-icon">✓</div>
          <h2>Request Submitted Successfully</h2>
          <div className="njar-success-ref">{success.requestNumber}</div>
          <div className="njar-success-row"><strong>Employee:</strong> {success.employeeName}</div>
          <div className="njar-success-row"><strong>Joining:</strong> {success.joiningDate}</div>
          <p style={{ marginTop: 18, color: "var(--muted)" }}>The IT team has been notified and will prepare the requested assets before the joining date.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="njar-root">
      <div className="njar-topbar">
        <img className="njar-topbar-logo" src={LOGO_SRC} alt="Jaipuria IT360 Desk" onError={(e) => { e.target.style.display = "none"; }} />
        <span className="njar-topbar-brand">Jaipuria IT360 Desk</span>
      </div>

      <div className="njar-shell">
        <div className="njar-eyebrow">New Joiner</div>
        <div className="njar-eyebrow-rule" />
        <h1 className="njar-title">Asset Requirement Request</h1>
        <p className="njar-subtitle">Submit IT asset requirements before the employee's joining date. No login required.</p>

        <form onSubmit={handleSubmit}>
          {submitError && <div className="njar-alert njar-alert-error">{submitError}</div>}

          <div className="njar-card">
            <div className="njar-section-title">New Employee Details</div>
            <div className="njar-grid">
              <div className="njar-field">
                <label className="njar-label">Employee Name<span className="njar-req">*</span></label>
                <input className="njar-input" value={form.employee_name} onChange={(e) => setField("employee_name", e.target.value)} required />
              </div>
              <div className="njar-field">
                <label className="njar-label">Employee Username<span className="njar-req">*</span></label>
                <input className="njar-input" value={form.employee_username} onChange={(e) => setField("employee_username", e.target.value)} placeholder="e.g. rahul.sharma" required />
              </div>
              <div className="njar-field">
                <label className="njar-label">Employee Code</label>
                <input className="njar-input" value={form.employee_code} onChange={(e) => setField("employee_code", e.target.value)} />
              </div>
              <div className="njar-field">
                <label className="njar-label">Designation<span className="njar-req">*</span></label>
                <input className="njar-input" value={form.designation} onChange={(e) => setField("designation", e.target.value)} required />
              </div>
              <div className="njar-field">
                <label className="njar-label">Department<span className="njar-req">*</span></label>
                <select className="njar-select" value={form.department_id} onChange={(e) => setField("department_id", e.target.value)} required>
                  <option value="">Select Department</option>
                  {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div className="njar-field">
                <label className="njar-label">Company / Unit<span className="njar-req">*</span></label>
                <select className="njar-select" value={form.company_id} onChange={(e) => { setField("company_id", e.target.value); setField("manager_user_id", ""); }} required>
                  <option value="">Select Company</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="njar-field">
                <label className="njar-label">Reporting Manager<span className="njar-req">*</span></label>
                <select className="njar-select" value={form.manager_user_id} onChange={(e) => setField("manager_user_id", e.target.value)} required disabled={managersLoading}>
                  <option value="">{managersLoading ? "Loading..." : "Select Manager"}</option>
                  {managers.map((m) => <option key={m.id} value={m.id}>{m.name}{m.designation ? ` — ${m.designation}` : ""}</option>)}
                </select>
              </div>
              <div className="njar-field">
                <label className="njar-label">Joining Date<span className="njar-req">*</span></label>
                <input type="date" className="njar-input" value={form.joining_date} onChange={(e) => setField("joining_date", e.target.value)} required />
              </div>
              <div className="njar-field">
                <label className="njar-label">Official Email</label>
                <input type="email" className="njar-input" value={form.official_email} onChange={(e) => setField("official_email", e.target.value)} />
              </div>
              <div className="njar-field">
                <label className="njar-label">Location / Office</label>
                <input className="njar-input" value={form.location} onChange={(e) => setField("location", e.target.value)} />
              </div>
              <div className="njar-field njar-field-full">
                <label className="njar-label">Remarks</label>
                <textarea className="njar-textarea" value={form.remarks} onChange={(e) => setField("remarks", e.target.value)} />
              </div>
            </div>
          </div>

          <div className="njar-card">
            <div className="njar-section-title">Assets Required</div>
            {assetTypes.length === 0 && <p className="njar-field-hint">Loading asset options…</p>}
            {assetTypes.map((t) => {
              const sel = selected[t.id] || { checked: false, qty: 1, note: "" };
              return (
                <div key={t.id} className={`njar-asset-row${sel.checked ? " is-checked" : ""}`}>
                  <input type="checkbox" className="njar-asset-checkbox" checked={sel.checked} onChange={() => toggleAsset(t.id)} />
                  <span className="njar-asset-name">{t.name}</span>
                  {sel.checked ? (
                    <>
                      <input type="number" min={1} max={50} className="njar-asset-qty" value={sel.qty} onChange={(e) => setAssetQty(t.id, e.target.value)} placeholder="Qty" />
                      <input className="njar-asset-note" value={sel.note} onChange={(e) => setAssetNote(t.id, e.target.value)} placeholder="Optional requirement (e.g. Developer specification)" />
                    </>
                  ) : <span />}
                </div>
              );
            })}
          </div>

          <div className="njar-card">
            <div className="njar-section-title">Your Details (Optional)</div>
            <div className="njar-grid">
              <div className="njar-field">
                <label className="njar-label">Your Name</label>
                <input className="njar-input" value={form.requester_name} onChange={(e) => setField("requester_name", e.target.value)} />
              </div>
              <div className="njar-field">
                <label className="njar-label">Your Email</label>
                <input type="email" className="njar-input" value={form.requester_email} onChange={(e) => setField("requester_email", e.target.value)} />
              </div>
            </div>
          </div>

          <div className="njar-submit-row">
            <button type="submit" className="njar-btn" disabled={submitting}>
              {submitting ? "Submitting..." : "Submit Request"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
